import { createPortal } from "react-dom";
import { useUiLanguage } from "@/i18n/useUiLanguage";
import { tr } from "@/i18n/copy";
import PushTestButton from "@/components/PushTestButton";
import { useEffect, useMemo, useState } from "react";
import { BellRing, CheckCircle2, Download, Share, Smartphone, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { api } from "@/lib/api";
import { enablePushNotifications, getPushDeviceState, pushPlatformSupport } from "@/pwa";
import { getCookiePreferences } from "@/components/CookiePreferences";
import { clientAppPresentation, studioDisplayName, studioBrandStyle, clientVisualStyleClass } from "@/studioBranding";

function setupKey(studio) {
  return `tps:client-app-setup:v1:${studio?.brand_network?.slug || studio?.slug || studio?.id || studioDisplayName(studio || {})}`;
}

function platformInfo() {
  const ua = navigator.userAgent || "";
  const support = pushPlatformSupport();
  return {
    ...support,
    android: /Android/i.test(ua),
    mac: /Macintosh|Mac OS X/i.test(ua) && !support.isiOS,
    windows: /Windows/i.test(ua),
  };
}

export default function ClientAppSetup({ studio, relationshipId, open = false, onClose }) {
  useUiLanguage();
  const [visible, setVisible] = useState(false);
  const [step, setStep] = useState("install");
  const [installPrompt, setInstallPrompt] = useState(null);
  const [pushStatus, setPushStatus] = useState(null);
  const [pushBusy, setPushBusy] = useState(false);
  const [installed, setInstalled] = useState(false);
  const platform = useMemo(() => platformInfo(), []);
  const studioName = studio?.brand_network?.name || studioDisplayName(studio || {});
  const branding = clientAppPresentation(studio || {});
  const app = clientAppPresentation(studio || {});

  const refreshStatus = async () => {
    try { setPushStatus(await getPushDeviceState(api, relationshipId)); } catch { /* Preserve the last confirmed state while offline. */ }
    setInstalled(pushPlatformSupport().standalone);
  };

  useEffect(() => {
    const beforeInstall = (event) => { event.preventDefault(); setInstallPrompt(event); };
    const installedEvent = () => { setInstalled(true); setInstallPrompt(null); setStep("push"); };
    window.addEventListener("beforeinstallprompt", beforeInstall);
    window.addEventListener("appinstalled", installedEvent);
    return () => {
      window.removeEventListener("beforeinstallprompt", beforeInstall);
      window.removeEventListener("appinstalled", installedEvent);
    };
  }, []);

  useEffect(() => {
    if (!studio || !relationshipId) return;
    const privacyReady = Boolean(getCookiePreferences());
    let alreadySeen = false;
    try { alreadySeen = Boolean(localStorage.getItem(setupKey(studio))); } catch {}
    if (privacyReady && !alreadySeen) setVisible(true);
    refreshStatus();
    const privacySaved = () => {
      let seen = false;
      try { seen = Boolean(localStorage.getItem(setupKey(studio))); } catch {}
      if (!seen) setVisible(true);
    };
    window.addEventListener("tps:privacy-preferences", privacySaved);
    return () => window.removeEventListener("tps:privacy-preferences", privacySaved);
  }, [studio?.slug, relationshipId]);

  useEffect(() => {
    if (!relationshipId) return;
    window.addEventListener("tps:push-state-changed", refreshStatus);
    return () => window.removeEventListener("tps:push-state-changed", refreshStatus);
  }, [relationshipId]);

  useEffect(() => { if (open) { setVisible(true); refreshStatus(); } }, [open]);

  const finish = () => {
    try { localStorage.setItem(setupKey(studio), JSON.stringify({ seen_at: new Date().toISOString() })); } catch {}
    setVisible(false);
    onClose?.();
  };

  const install = async () => {
    if (installPrompt) {
      await installPrompt.prompt();
      await installPrompt.userChoice;
      setInstallPrompt(null);
      setInstalled(pushPlatformSupport().standalone);
      setStep("push");
      return;
    }
    setStep("push");
  };

  const enablePush = async () => {
    setPushBusy(true);
    try {
      const enabled = await enablePushNotifications(api, relationshipId);
      await refreshStatus();
      if (enabled) setStep("done");
    } finally { setPushBusy(false); }
  };

  if (!visible) return null;

  const currentSupport = pushPlatformSupport();
  const notificationPermission = typeof window !== "undefined" && "Notification" in window ? window.Notification.permission : "unsupported";
  const activePush = Boolean(pushStatus?.device_active) && notificationPermission === "granted";

  return createPortal(<div className={`client-branded-shell ${clientVisualStyleClass(studio)} fixed inset-0 z-[240] grid place-items-end bg-black/65 p-3 sm:place-items-center sm:p-6`} style={studioBrandStyle(studio)} data-testid="client-app-setup">
    <Card className="relative w-full max-w-xl border bg-card/98 p-5 shadow-2xl backdrop-blur-xl sm:p-6">
      <button onClick={finish} aria-label={tr("Chiudi")} className="absolute right-3 top-3 rounded-lg p-2 text-muted-foreground hover:bg-white/10"><X className="h-4 w-4"/></button>
      <div className="flex items-center gap-3 pr-9">{app.public_logo_url&&<img src={app.public_logo_url} alt={tr("Logo {{0}}", {"0": studioName})} className="h-12 w-12 rounded-2xl border object-cover"/>}<div><p className="label-tiny" style={{color:branding.primary_color}}>{tr("App e notifiche")}</p><p className="mt-1 font-display text-lg font-bold">{studioName}</p></div></div>
      <h2 className="mt-4 font-display text-2xl font-bold">{step === "install" ? tr("Porta lo Studio sul tuo dispositivo") : step === "push" ? tr("Resta aggiornato in tempo reale") : tr("Configurazione completata")}</h2>

      {step === "install" && <div className="mt-5 space-y-4">
        <p className="text-sm leading-6 text-muted-foreground">{tr("Installa l’app di")} <b className="text-foreground">{studioName}</b> {tr("per aprirla come una vera app e ricevere più facilmente appuntamenti, consensi, preventivi, bonus, aftercare e altri aggiornamenti.")}</p>
        <div className="rounded-2xl border p-4 text-sm">
          {platform.isiOS && !installed ? <><div className="flex gap-3"><Share className="mt-0.5 h-5 w-5 shrink-0 text-primary"/><div><b>{tr("iPhone / iPad")}</b><p className="mt-1 text-muted-foreground">{tr("In Safari tocca")} <b>{tr("Condividi")}</b>{tr(", poi")} <b>{tr("Aggiungi alla schermata Home")}</b>{tr(". Verifica che compaiano")} <b>{studioName}</b> {tr("e il logo dello Studio, quindi conferma e apri l’app dalla nuova icona.")}</p></div></div></> :
           installed ? <div className="flex items-center gap-3 text-emerald-600"><CheckCircle2 className="h-5 w-5"/><b>{tr("App già installata su questo dispositivo.")}</b></div> :
           <div className="flex gap-3"><Download className="mt-0.5 h-5 w-5 shrink-0 text-primary"/><div><b>{platform.android ? "Android" : platform.mac ? "Mac" : platform.windows ? tr("PC Windows") : tr("Questo dispositivo")}</b><p className="mt-1 text-muted-foreground">{installPrompt ? tr("Tocca Installa app e conferma dal browser.") : tr("Apri il menu del browser e scegli Installa app / Aggiungi alla schermata Home quando disponibile.")}</p></div></div>}
        </div>
        <div className="flex flex-wrap gap-2">
          {installPrompt && <Button onClick={install}><Download className="mr-2 h-4 w-4"/>{tr("Installa app")}</Button>}
          <Button variant={installPrompt ? "outline" : "default"} onClick={() => setStep("push")}>{platform.isiOS && !installed ? tr("Ho aggiunto l’app alla Home") : tr("Continua")}</Button>
          <Button variant="ghost" onClick={finish}>{tr("Lo farò dopo")}</Button>
        </div>
      </div>}

      {step === "push" && <div className="mt-5 space-y-4">
        <p className="text-sm leading-6 text-muted-foreground">{tr("Le notifiche di")} <b className="text-foreground">{studioName}</b> {tr("ti avvisano in modo immediato quando cambia qualcosa che ti riguarda. Toccando la notifica verrai portato direttamente alla sezione corretta dell’app.")}</p>
        <div className="grid gap-2 text-sm sm:grid-cols-2">
          <div className="rounded-xl border p-3"><BellRing className="mb-2 h-5 w-5 text-primary"/><b>{tr("Ricevi aggiornamenti")}</b><p className="mt-1 text-xs text-muted-foreground">{tr("Appuntamenti, promemoria, consensi, preventivi, pagamenti, bonus, aftercare e recensioni.")}</p></div>
          <div className="rounded-xl border p-3"><Smartphone className="mb-2 h-5 w-5 text-primary"/><b>{tr("Notifiche cliccabili")}</b><p className="mt-1 text-xs text-muted-foreground">{tr("Ogni avviso apre direttamente l’attività da controllare o completare.")}</p></div>
        </div>
        {platform.isiOS && !currentSupport.standalone && <p className="rounded-xl border border-sky-500/30 bg-sky-500/10 p-3 text-sm text-sky-700">{tr("Su iPhone/iPad, per attivare le Push devi prima aprire questa app dall’icona aggiunta alla schermata Home.")}</p>}
        {notificationPermission === "denied" && <p className="rounded-xl border border-sky-500/30 bg-sky-500/10 p-3 text-sm text-sky-700">{tr("Le notifiche sono bloccate nelle impostazioni del dispositivo/browser. Riattivale per")} {studioName} {tr("e torna qui.")}</p>}
        {activePush ? <div className="flex items-center gap-2 rounded-xl border border-emerald-500/30 bg-emerald-500/10 p-3 text-sm text-emerald-600"><CheckCircle2 className="h-5 w-5"/><b>{tr("Notifiche Push già attive.")}</b></div> :
        <div className="flex flex-wrap gap-2">
          <Button onClick={enablePush} disabled={pushBusy || !currentSupport.canEnable || notificationPermission === "denied"}><BellRing className="mr-2 h-4 w-4"/>{pushBusy ? tr("Attivazione…") : tr("Attiva notifiche Push")}</Button>
          <Button variant="ghost" onClick={finish}>{tr("Non ora")}</Button>
        </div>}
        {(activePush || step === "done") && <Button onClick={finish}>{tr("Fatto")}</Button>}
      </div>}

      {step === "done" && <div className="mt-5 space-y-4"><div className="flex items-start gap-3 rounded-2xl border border-emerald-500/30 bg-emerald-500/10 p-4"><CheckCircle2 className="mt-0.5 h-5 w-5 text-emerald-600"/><div><b>{tr("Notifiche attive per")} {studioName}</b><p className="mt-1 text-sm text-muted-foreground">{tr("Riceverai gli aggiornamenti importanti anche quando l’app non è aperta.")}</p></div></div><Button onClick={finish}>{tr("Entra nell’app")}</Button></div>}
      {(step === "push" || step === "done") && <PushTestButton relationshipId={relationshipId} enabled={activePush} />}
    </Card>
  </div>, document.body);
}
